import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLang, useTheme, fmt, Stars, Notif } from '../App';
import { computeSkills } from '../utils/helpers';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, Tooltip,
} from 'recharts';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, rectSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/* ══════════════════════════════════════════
   MOBILE HOOK
══════════════════════════════════════════ */
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
};

/* ══════════════════════════════════════════
   OVERLAY MODAL WRAPPER
══════════════════════════════════════════ */
const Modal = ({ onClose, title, children, width = 360 }) => (
  <div
    style={{
      position: 'fixed', inset: 0, zIndex: 3000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)',
    }}
    onClick={onClose}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{
        width, maxWidth: 'calc(100vw - 40px)',
        background: 'var(--card2)', border: '1px solid var(--border)',
        borderRadius: 20, padding: 24,
        boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
        animation: 'fadeUp 0.2s cubic-bezier(.34,1.4,.64,1) both',
        position: 'relative', maxHeight: '90vh', overflowY: 'auto',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
        <span style={{ fontWeight: 800, fontSize: 14, color: 'var(--a3)' }}>{title}</span>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', color: 'var(--muted)',
          cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: '0 4px',
        }}>×</button>
      </div>
      {children}
    </div>
  </div>
);

/* ══════════════════════════════════════════
   SOCIAL META
══════════════════════════════════════════ */
const SOCIAL_META = {
  discord: {
    label: 'Discord', color: '#5865F2', bg: 'rgba(88,101,242,0.15)',
    icon: <img src="https://cdn.simpleicons.org/discord/ffffff" width="13" height="13" alt="discord" />,
    placeholder: 'username', isLink: false,
  },
  telegram: {
    label: 'Telegram', color: '#26A5E4', bg: 'rgba(38,165,228,0.15)',
    icon: <img src="https://cdn.simpleicons.org/telegram/26A5E4" width="13" height="13" alt="telegram" />,
    placeholder: '@username', isLink: true, linkPrefix: 'https://t.me/',
  },
  twitch: {
    label: 'Twitch', color: '#9146FF', bg: 'rgba(145,70,255,0.15)',
    icon: <img src="https://cdn.simpleicons.org/twitch/9146FF" width="13" height="13" alt="twitch" />,
    placeholder: 'username', isLink: true, linkPrefix: 'https://twitch.tv/',
  },
  steam: {
    label: 'Steam', color: '#1b9bd1', bg: 'rgba(27,155,209,0.15)',
    icon: <img src="https://cdn.simpleicons.org/steam/1b9bd1" width="13" height="13" alt="steam" />,
    placeholder: 'username', isLink: true, linkPrefix: 'https://steamcommunity.com/id/',
  },
  vk: {
    label: 'VK', color: '#4a76a8', bg: 'rgba(74,118,168,0.15)',
    icon: <img src="https://cdn.simpleicons.org/vk/4a76a8" width="13" height="13" alt="vk" />,
    placeholder: '@username', isLink: true, linkPrefix: 'https://vk.com/',
  },
};

/* ══════════════════════════════════════════
   WIDGET META
══════════════════════════════════════════ */
const WIDGET_META = {
  topplay:    { label: 'Top Play',       labelRu: 'Лучшая игра',      size: 'small'  },
  activity:   { label: 'Activity',       labelRu: 'Активность',       size: 'small'  },
  setup:      { label: 'Setup',          labelRu: 'Сетап',            size: 'small'  },
  session:    { label: 'Last Session',   labelRu: 'Вчера',            size: 'small'  },
  stats:      { label: 'Statistics',     labelRu: 'Статистика',       size: 'medium' },
  skills:     { label: 'Skills',         labelRu: 'Навыки',           size: 'medium' },
  radar:      { label: 'Skill Radar',    labelRu: 'Радар навыков',    size: 'medium' },
  mostplayed: { label: 'Most Played',    labelRu: 'Больше всего',     size: 'medium' },
  playcount:  { label: 'Play History',   labelRu: 'История игр',      size: 'large'  },
};

// large=4col (desktop) / 2col (mobile)
// medium=2col (desktop) / 2col (mobile)
// small=1col
const ALL_WIDGET_KEYS = Object.keys(WIDGET_META);
const DEFAULT_WIDGETS = ['stats', 'skills', 'topplay', 'activity', 'radar', 'mostplayed'];

/* ══════════════════════════════════════════
   PRESET COLORS
══════════════════════════════════════════ */
const PRESET_COLORS = ['#ff6eb4','#a78bfa','#60a5fa','#34d399','#fbbf24','#f87171','#e879f9','#22d3ee'];
const STATUS_IDEAS  = ['grinding DT 🎯','retired 💤','chilling 🌸','improving acc 🎯','farming pp 📈','taking a break ☕'];

const EditBtn = ({ onClick, label, color }) => (
  <button onClick={onClick} style={{
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '3px 10px', borderRadius: 20, cursor: 'pointer',
    background: color ? `${color}15` : 'var(--bg2)',
    border: `1px dashed ${color || 'var(--border)'}`,
    color: color || 'var(--muted)', fontSize: 11, fontFamily: 'inherit',
  }}>✎ {label}</button>
);

/* ══════════════════════════════════════════
   WIDGETS
   ВАЖНО: каждый виджет имеет flex:1 на
   корневом div, чтобы растянуться до
   высоты ячейки грида (flex chain)
══════════════════════════════════════════ */
const WidgetTopPlay = ({ user, lang }) => {
  const tp = user?.topPlay || user?.top_play;
  if (!tp) return null;
  return (
    <div className="card fu" style={{ padding: 14, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--a3)', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 8 }}>
        ★ {lang === 'ru' ? 'Лучшая игра' : 'Top Play'}
      </div>
      <div className="mono" style={{ fontSize: 26, fontWeight: 800, color: 'var(--a)', lineHeight: 1, marginBottom: 6 }}>
        {tp.pp}<span style={{ fontSize: 13, fontWeight: 600, marginLeft: 2 }}>pp</span>
      </div>
      <div style={{ fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--text)', marginBottom: 4 }}>{tp.map}</div>
      <Stars v={tp.stars} />
    </div>
  );
};

const WidgetStats = ({ user, lang }) => {
  const rows = [
    [lang === 'ru' ? 'PP' : 'Performance', `${fmt(Math.round(user.pp || 0))}pp`],
    [lang === 'ru' ? 'Точность' : 'Accuracy', `${user.accuracy || 0}%`],
    [lang === 'ru' ? 'Всего игр' : 'Play Count', fmt(user.play_count || 0)],
    [lang === 'ru' ? 'Время' : 'Play Time', user.play_time || '0h'],
    [lang === 'ru' ? 'Макс. комбо' : 'Max Combo', `${fmt(user.max_combo || 0)}x`],
  ];
  return (
    <div className="card fu" style={{ padding: 18, flex: 1 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--a3)', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 12 }}>
        ◈ {lang === 'ru' ? 'Статистика' : 'Statistics'}
      </div>
      {rows.map(([k, v]) => (
        <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>{k}</span>
          <span className="mono" style={{ fontSize: 14, fontWeight: 700 }}>{v}</span>
        </div>
      ))}
    </div>
  );
};

const WidgetSkills = ({ user, lang }) => {
  let skills = [];
  try { skills = computeSkills(user) || []; } catch {}
  if (!skills.length) return null;
  return (
    <div className="card fu" style={{ padding: 18, flex: 1 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--a3)', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 12 }}>
        ◉ {lang === 'ru' ? 'Навыки' : 'Skills'}
      </div>
      {skills.map(s => (
        <div key={s.skill} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 9 }}>
          <div style={{ width: 66, fontSize: 11, color: 'var(--muted)' }}>{lang === 'ru' ? s.skillRu : s.skill}</div>
          <div className="pb-wrap" style={{ flex: 1, height: 5 }}><div className="pb" style={{ width: `${s.value}%`, height: '100%' }} /></div>
          <span className="mono" style={{ width: 26, textAlign: 'right', fontSize: 13, fontWeight: 700 }}>{s.value}</span>
        </div>
      ))}
    </div>
  );
};

const WidgetActivity = ({ user, lang }) => {
  const now = Date.now();
  const recent24 = (user?.recentActivity || []).filter(pl => pl.timestamp && (now - new Date(pl.timestamp).getTime()) < 86_400_000);
  const totalPlays = recent24.length;
  const failCount  = recent24.filter(pl => pl.rank === 'F').length;
  const best24PP   = (() => {
    const b = (user?.bestPlays || []).filter(pl => pl.timestamp && (now - new Date(pl.timestamp).getTime()) < 86_400_000);
    return b.length ? Math.max(...b.map(pl => pl.pp || 0)) : null;
  })();
  return (
    <div className="card fu" style={{ padding: 14, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--a3)', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 10 }}>
        ⚡ {lang === 'ru' ? 'Сегодня' : 'Today'}
      </div>
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 2 }}>{lang === 'ru' ? 'Игр' : 'Plays'}</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span className="mono" style={{ fontSize: 26, fontWeight: 800, color: '#7eb8f7', lineHeight: 1 }}>{totalPlays}</span>
          {failCount > 0 && <span style={{ fontSize: 10, color: '#f87171' }}>{failCount} fail</span>}
        </div>
      </div>
      <div>
        <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 2 }}>PP today</div>
        {best24PP !== null
          ? <span className="mono" style={{ fontSize: 20, fontWeight: 800, color: '#68d891', lineHeight: 1 }}>+{best24PP}<span style={{ fontSize: 11, marginLeft: 2 }}>pp</span></span>
          : <span style={{ fontSize: 11, color: 'rgba(148,163,184,0.4)' }}>—</span>
        }
      </div>
    </div>
  );
};

const WidgetRadar = ({ user, lang }) => {
  let skills = [];
  try { skills = computeSkills(user) || []; } catch {}
  if (!skills.length) return null;
  return (
    <div className="card fu" style={{ padding: 18, flex: 1, display: 'flex', flexDirection: 'column' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--a3)', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 8 }}>
        ◉ {lang === 'ru' ? 'Радар навыков' : 'Skill Radar'}
      </div>
      {/* flex:1 + height:100% — чарт заполняет всё свободное место */}
      <div style={{ flex: 1, minHeight: 150 }}>
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={skills} outerRadius="55%" margin={{ top: 10, right: 20, bottom: 10, left: 20 }}>
            <PolarGrid stroke="var(--border)" />
            <PolarAngleAxis dataKey={lang === 'ru' ? 'skillRu' : 'skill'} tick={{ fill: 'var(--muted)', fontSize: 9 }} />
            <Radar dataKey="value" stroke="var(--a)" fill="var(--dim)" strokeWidth={2} />
          </RadarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const WidgetPlaycount = ({ user, lang }) => {
  const raw = user?.monthly_playcounts || [];
  if (!raw.length) return null;

  // start_date = "2026-01" → парсим вручную, без new Date() чтобы не было Invalid Date
  const data = raw.map(m => {
    const str = m.start_date || m.month || '';
    const [year, month] = str.split('-');
    const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const label = month ? MONTHS[parseInt(month, 10) - 1] || str : str;
    return { month: label, count: m.count || 0 };
  });

  return (
    <div className="card fu" style={{ padding: 18, flex: 1, display: 'flex', flexDirection: 'column' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--a3)', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 12 }}>
        ▶ {lang === 'ru' ? 'Playcount · 3 месяца' : 'Play History · 3 months'}
      </div>
      <div style={{ flex: 1, minHeight: 120 }}>
        <ResponsiveContainer width="100%" height={150}>
          <BarChart data={data} barSize={40}>
            <XAxis dataKey="month" tick={{ fill: 'var(--muted)', fontSize: 12 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: 'var(--muted)', fontSize: 12 }} axisLine={false} tickLine={false} width={36} />
            <Tooltip
              contentStyle={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 12 }}
              labelStyle={{ color: 'var(--text)' }}
              itemStyle={{ color: 'var(--text)' }}
              cursor={{ fill: 'rgba(255,255,255,0.04)' }}
              formatter={v => [v, lang === 'ru' ? 'Игр' : 'Plays']}
            />
            <Bar dataKey="count" fill="var(--a)" radius={[8, 8, 0, 0]} opacity={0.85} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const WidgetMostPlayed = ({ user, lang }) => {
  const maps = user?.mostPlayed || [];
  if (!maps.length) return (
    <div className="card fu" style={{ padding: 20, flex: 1 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--a3)', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 8 }}>
        ♪ {lang === 'ru' ? 'Больше всего сыграно' : 'Most Played'}
      </div>
      <div style={{ color: 'var(--muted)', fontSize: 12 }}>{lang === 'ru' ? 'Нет данных' : 'No data'}</div>
    </div>
  );
  return (
    <div className="card fu" style={{ padding: 20, flex: 1 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--a3)', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 12 }}>
        ♪ {lang === 'ru' ? 'Больше всего сыграно' : 'Most Played'}
        <span style={{ marginLeft: 8, padding: '1px 8px', borderRadius: 20, background: 'var(--dim)', color: 'var(--a)', fontSize: 10, fontWeight: 700 }}>{maps[0]?.count || 0}</span>
      </div>
      {maps.map((m, i) => (
        <div key={m.beatmapset_id || i} style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '9px 0',
          borderBottom: i < maps.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none',
        }}>
          {m.cover_url
            ? <img src={m.cover_url} alt="" style={{ width: 48, height: 48, borderRadius: 9, flexShrink: 0, objectFit: 'cover', border: '1px solid rgba(255,255,255,0.08)', display: 'block' }} />
            : <div style={{ width: 48, height: 48, borderRadius: 9, flexShrink: 0, background: 'var(--bg2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🎵</div>
          }
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.title}</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
              {m.artist || m.mapper}
              {m.diff && <span style={{ marginLeft: 6, opacity: 0.6 }}>[{m.diff}]</span>}
            </div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div className="mono" style={{ fontSize: 15, fontWeight: 800, color: 'var(--amber)' }}>{m.count}</div>
            <div style={{ fontSize: 9, color: 'var(--muted)', marginTop: 1 }}>plays</div>
          </div>
        </div>
      ))}
    </div>
  );
};

const WidgetSetup = ({ user, lang, isReadOnly, onSave }) => {
  const [editing, setEditing] = useState(false);
  const setup0 = user?.profileSetup || {};
  const [draft, setDraft] = useState(setup0);
  useEffect(() => { setDraft(user?.profileSetup || {}); }, [user]);

  const fields = [
    { key: 'tablet',   icon: '⬡', label: lang === 'ru' ? 'Планшет'    : 'Tablet'   },
    { key: 'keyboard', icon: '⌨', label: lang === 'ru' ? 'Клавиатура' : 'Keyboard' },
    { key: 'monitor',  icon: '🖥', label: lang === 'ru' ? 'Монитор'    : 'Monitor'  },
    { key: 'mouse',    icon: '🖱', label: lang === 'ru' ? 'Мышь'       : 'Mouse'    },
  ];
  const hasAny = fields.some(f => setup0[f.key]);
  if (!hasAny && isReadOnly) return null;

  return (
    <div className="card fu" style={{ padding: 14, flex: 1 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--a3)', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 10 }}>
        ⌨ {lang === 'ru' ? 'Сетап' : 'Setup'}
      </div>
      {fields.map((f, fi) => (
        <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: fi < fields.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
          <span style={{ fontSize: 13, width: 18, textAlign: 'center', flexShrink: 0, opacity: 0.6 }}>{f.icon}</span>
          <span style={{ fontSize: 11, color: 'var(--muted)', width: 60, flexShrink: 0 }}>{f.label}</span>
          {editing
            ? <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
                <input
                  value={draft[f.key] || ''}
                  onChange={e => setDraft(d => ({ ...d, [f.key]: e.target.value }))}
                  placeholder="..."
                  style={{ flex: 1, padding: '3px 7px', borderRadius: 6, fontSize: 11, background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none', fontFamily: 'inherit' }}
                />
                {draft[f.key] && (
                  <button onClick={() => setDraft(d => ({ ...d, [f.key]: '' }))} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 13, padding: '0 2px', lineHeight: 1, flexShrink: 0 }}>×</button>
                )}
              </div>
            : <span style={{ fontSize: 11, fontWeight: setup0[f.key] ? 600 : 400, color: setup0[f.key] ? 'var(--text)' : 'rgba(148,163,184,0.45)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                {setup0[f.key] || '—'}
              </span>
          }
        </div>
      ))}
      {!isReadOnly && (
        editing ? (
          <button onClick={() => { onSave(draft); setEditing(false); }} className="btn-accent" style={{ width: '100%', padding: '7px', fontSize: 12, borderRadius: 8, marginTop: 10 }}>
            {lang === 'ru' ? 'Сохранить' : 'Save'}
          </button>
        ) : (
          <button onClick={() => setEditing(true)} style={{
            marginTop: 10, width: '100%', background: 'none',
            border: '1px dashed var(--border)', borderRadius: 8,
            color: 'var(--muted)', cursor: 'pointer', fontSize: 11,
            padding: '5px', fontFamily: 'inherit',
          }}>
            ✎ {lang === 'ru' ? 'Редактировать' : 'Edit'}
          </button>
        )
      )}
    </div>
  );
};

const WidgetSession = ({ user, lang }) => {
  const yesterday = (() => { const d = new Date(); d.setDate(d.getDate() - 1); return d.toDateString(); })();
  const yPlays   = (user?.recentActivity || []).filter(pl => pl.timestamp && new Date(pl.timestamp).toDateString() === yesterday);
  const totalY   = yPlays.length;
  const passed   = yPlays.filter(pl => pl.rank !== 'F').length;
  const bestPP   = yPlays.filter(pl => pl.rank !== 'F').reduce((m, pl) => Math.max(m, pl.pp || 0), 0);

  if (totalY === 0) return (
    <div className="card fu" style={{ padding: 14, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--a3)', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 8 }}>
        🕐 {lang === 'ru' ? 'Вчера' : 'Yesterday'}
      </div>
      <div style={{ color: 'var(--muted)', fontSize: 11, opacity: 0.5 }}>—</div>
    </div>
  );

  return (
    <div className="card fu" style={{ padding: 14, flex: 1 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--a3)', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 10 }}>
        🕐 {lang === 'ru' ? 'Вчера' : 'Yesterday'}
      </div>
      {[
        { l: lang === 'ru' ? 'Игр'    : 'Plays',   v: totalY,          c: '#7eb8f7' },
        { l: lang === 'ru' ? 'Прошло' : 'Passed',  v: passed,          c: '#68d891' },
        ...(bestPP > 0 ? [{ l: lang === 'ru' ? 'Лучший PP' : 'Best PP', v: `${bestPP}pp`, c: 'var(--amber)' }] : []),
      ].map((row, i, arr) => (
        <div key={row.l} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
          <span style={{ fontSize: 11, color: 'var(--muted)' }}>{row.l}</span>
          <span className="mono" style={{ fontSize: 14, fontWeight: 700, color: row.c }}>{row.v}</span>
        </div>
      ))}
    </div>
  );
};

/* ══════════════════════════════════════════
   SORTABLE WIDGET WRAPPER (dnd-kit)

   Flex chain:
   [grid cell div] display:flex; align-self:stretch
     └─ [SortableWidget div] display:flex; flex-direction:column; flex:1; height:100%
          └─ [Widget root div.card] flex:1  ← растягивает фон/бордер
══════════════════════════════════════════ */
const SortableWidget = ({ id, children, isReadOnly }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
    /* flex chain: этот div занимает всё пространство ячейки */
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    height: '100%',
  };
  return (
    <div ref={setNodeRef} style={style}>
      {!isReadOnly && (
        <div
          {...attributes} {...listeners}
          style={{
            position: 'absolute', top: 8, right: 8, zIndex: 10,
            width: 20, height: 20, borderRadius: 6,
            background: 'rgba(0,0,0,0.4)', border: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'grab', fontSize: 11, color: 'var(--muted)',
            userSelect: 'none',
          }}
        >⋮⋮</div>
      )}
      {children}
    </div>
  );
};

/* Статичная обёртка для read-only (без dnd-kit) */
const StaticWidget = ({ children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', flex: 1, height: '100%' }}>
    {children}
  </div>
);

const WIDGET_COMPONENTS = {
  topplay:    WidgetTopPlay,
  stats:      WidgetStats,
  skills:     WidgetSkills,
  activity:   WidgetActivity,
  radar:      WidgetRadar,
  playcount:  WidgetPlaycount,
  mostplayed: WidgetMostPlayed,
  setup:      WidgetSetup,
  session:    WidgetSession,
};

/* ══════════════════════════════════════════
   FAV MAP CARD
══════════════════════════════════════════ */
const FavMapCard = ({ map, onRemove, isReadOnly, lang, accentColor }) => {
  if (!map) return null;
  const href = map.url || (map.id ? `https://osu.ppy.sh/beatmapsets/${map.id}` : null);
  return (
    <a href={href || '#'} target={href ? '_blank' : undefined} rel="noopener noreferrer"
      style={{ textDecoration: 'none', display: 'block', marginBottom: 8 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '10px 14px', borderRadius: 14,
        background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.1)',
        cursor: href ? 'pointer' : 'default', transition: 'opacity 0.15s',
        position: 'relative',
      }}
      onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
      onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
        {map.cover_url
          ? <img src={map.cover_url} alt="" style={{ width: 56, height: 42, borderRadius: 10, flexShrink: 0, objectFit: 'cover', border: '1px solid rgba(255,255,255,0.1)', display: 'block' }} />
          : <div style={{ width: 56, height: 42, borderRadius: 10, flexShrink: 0, background: 'var(--bg2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>🎵</div>
        }
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--text)' }}>
            {map.title || 'Unknown'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{map.artist || ''}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <span style={{ fontSize: 16, color: '#f87171' }}>♥</span>
          {!isReadOnly && onRemove && (
            <button onClick={e => { e.preventDefault(); e.stopPropagation(); onRemove(); }}
              style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 14, lineHeight: 1 }}>×</button>
          )}
        </div>
      </div>
    </a>
  );
};

/* ══════════════════════════════════════════
   УТИЛИТА
══════════════════════════════════════════ */
const isEq = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/* ══════════════════════════════════════════
   MAIN PROFILE
══════════════════════════════════════════ */
const Profile = ({ user, isReadOnly = false }) => {
  const { lang }   = useLang();
  const { theme }  = useTheme();
  const isMobile   = useIsMobile();

  const [status,  setStatus]  = useState(() => user?.profileStatus  || '');
  const [socials, setSocials] = useState(() => user?.profileSocials || {});
  const [widgets, setWidgets] = useState(() => {
    // Читаем из localStorage первым — мгновенно, без F5
    if (!isReadOnly && user?.id) {
      try {
        const local = localStorage.getItem(`osu_widgets_${user.id}`);
        if (local) return JSON.parse(local);
      } catch {}
    }
    return user?.profileWidgets || DEFAULT_WIDGETS;
  });
  const [color,   setColor]   = useState(() => user?.profileColor   || null);
  const [favMap,  setFavMap]  = useState(() => user?.profileFavMap  || null);
  const [customBanner, setCustomBanner] = useState(() => {
    const raw = user?.profileCustomBanner;
    if (!raw) return null;
    try { return typeof raw === 'object' ? raw : JSON.parse(raw); } catch { return { url: raw, dim: 0.78, posY: 30 }; }
  });
  const [draftBannerDim, setDraftBannerDim] = useState(0.78);
  const [draftBannerPosY, setDraftBannerPosY] = useState(30);
  const [isDraggingPreview, setIsDraggingPreview] = useState(false);
  const dragStartRef = useRef({ y: 0, posY: 30 });
  const [setup, setSetup] = useState(() => {
    if (!isReadOnly && user?.id) {
      try {
        const local = localStorage.getItem(`osu_setup_${user.id}`);
        if (local) return JSON.parse(local);
      } catch {}
    }
    return user?.setup || user?.profileSetup || {};
  });

  const [notif,     setNotif]     = useState(null);
  const [openModal, setOpenModal] = useState(null);
  const [draftStatus,  setDraftStatus]  = useState('');
  const [draftSocials, setDraftSocials] = useState({});
  const [draftWidgets, setDraftWidgets] = useState([]);
  const [draftColor,   setDraftColor]   = useState(null);
  const [draftFavUrl,    setDraftFavUrl]    = useState('');
  const [draftBannerUrl, setDraftBannerUrl] = useState('');

  /* dnd-kit sensors — активны только для своего профиля */
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  /* Умный useEffect — не ломает dnd-kit при re-renders */
useEffect(() => {
    if (!user) return;
    const isNewUser = prevUidRef.current !== user.id;
    prevUidRef.current = user.id;

    if (isNewUser) {
      // Новый юзер — жёсткий сброс без кадра со старыми данными
      setStatus (user.profileStatus  || '');
      setColor  (user.profileColor   || null);
      setSocials(user.profileSocials || {});
      // Виджеты: сначала localStorage, потом сервер
      const localW = (() => {
        if (isReadOnly || !user.id) return null;
        try { return JSON.parse(localStorage.getItem(`osu_widgets_${user.id}`) || ''); } catch { return null; }
      })();
      setWidgets(localW || user.profileWidgets || DEFAULT_WIDGETS);
      setFavMap        (user.profileFavMap        || null);
      setCustomBanner((() => {
        const raw = user.profileCustomBanner;
        if (!raw) return null;
        try { return typeof raw === 'object' ? raw : JSON.parse(raw); } catch { return { url: raw, dim: 0.78 }; }
      })());
      const localS = (() => {
        if (isReadOnly || !user.id) return null;
        try { return JSON.parse(localStorage.getItem(`osu_setup_${user.id}`) || ''); } catch { return null; }
      })();
      setSetup(localS || user.setup || user.profileSetup || {});
    } else {
      // Тот же юзер — умный апдейт, не ломает dnd-kit
      setStatus (prev => prev !== (user.profileStatus || '')     ? (user.profileStatus  || '')   : prev);
      setColor  (prev => prev !== (user.profileColor  || null)   ? (user.profileColor   || null) : prev);
      setSocials(prev => { const n = user.profileSocials || {};       return isEq(prev, n) ? prev : n; });
      // Виджеты: если есть localStorage — НЕ перетираем сервером
      setWidgets(prev => {
        const localW = (() => {
          if (isReadOnly || !user.id) return null;
          try { return JSON.parse(localStorage.getItem(`osu_widgets_${user.id}`) || ''); } catch { return null; }
        })();
        if (localW) return isEq(prev, localW) ? prev : localW;
        const n = user.profileWidgets || DEFAULT_WIDGETS;
        return isEq(prev, n) ? prev : n;
      });
      setFavMap       (prev => { const n = user.profileFavMap       || null; return isEq(prev, n) ? prev : n; });
      setCustomBanner(prev => {
        const raw = user.profileCustomBanner;
        const n = raw ? (() => { try { return typeof raw === 'object' ? raw : JSON.parse(raw); } catch { return { url: raw, dim: 0.78 }; } })() : null;
        return isEq(prev, n) ? prev : n;
      });
      setSetup(prev => {
        const localS = (() => {
          if (isReadOnly || !user.id) return null;
          try { return JSON.parse(localStorage.getItem(`osu_setup_${user.id}`) || ''); } catch { return null; }
        })();
        if (localS) return isEq(prev, localS) ? prev : localS;
        const n = user.profileSetup || user?.setup || {};
        return isEq(prev, n) ? prev : n;
      });
    }
  }, [user]);

  // Фетч кастомного баннера для чужого профиля
  useEffect(() => {
    if (!isReadOnly || !user?.id) return;
    fetch(`/api/get-profile?osu_id=${user.id}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data?.custom_banner) return;
        try {
          const parsed = typeof data.custom_banner === 'object' ? data.custom_banner : JSON.parse(data.custom_banner);
          setCustomBanner(parsed);
        } catch { setCustomBanner({ url: data.custom_banner, dim: 0.78, posY: 30 }); }
      })
      .catch(() => {});
  }, [user?.id, isReadOnly]);

  /// КОНЕЦ УМНОГО

  /* ── Авто-сохранение порядка виджетов ──────────────────────
     Срабатывает только если widgets отличаются от того,
     что пришло с сервера (user.profileWidgets).
     Использует существующий /api/sync-user.
  ─────────────────────────────────────────────────────────── */
  const saveTimeoutRef = useRef(null);
  const prevUidRef     = useRef(null);
  useEffect(() => {
    if (isReadOnly || !user?.id) return;
    if (isEq(widgets, user?.profileWidgets)) return;

    // Сохраняем в localStorage мгновенно — без дебаунса
    try { localStorage.setItem(`osu_widgets_${user.id}`, JSON.stringify(widgets)); } catch {}

    // Дебаунс 600ms — чтобы не спамить при быстром dnd
    clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      fetch('/api/sync-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          osu_id:        user.id,
          username:      user.username,
          profileUpdate: { widgets },
        }),
      }).catch(err => console.error('[Profile] auto-save widgets error:', err));
    }, 600);

    return () => clearTimeout(saveTimeoutRef.current);
  }, [widgets, user?.id, user?.username, user?.profileWidgets, isReadOnly]);

  const openEdit = (modal) => {
    if (modal === 'status')  setDraftStatus(status);
    if (modal === 'socials') setDraftSocials({ ...socials });
    if (modal === 'widgets') setDraftWidgets([...widgets]);
    if (modal === 'color')   setDraftColor(color);
    if (modal === 'favmap')  setDraftFavUrl('');
    if (modal === 'banner')  { setDraftBannerUrl(customBanner?.url || ''); setDraftBannerDim(customBanner?.dim ?? 0.78); setDraftBannerPosY(customBanner?.posY ?? 30); }
    setOpenModal(modal);
  };

  const saveProfile = useCallback(async (patch) => {
    if (isReadOnly || !user?.id) return;
    try {
      await fetch('/api/sync-user', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ osu_id: user.id, username: user.username, profileUpdate: patch }),
      });
      setNotif(lang === 'ru' ? 'Сохранено ✓' : 'Saved ✓');
    } catch { setNotif('Error'); }
  }, [user, isReadOnly, lang]);

  const commitStatus  = () => { setStatus(draftStatus); saveProfile({ status: draftStatus }); setOpenModal(null); };
  const commitSocials = () => {
    const cleaned = {};
    Object.entries(draftSocials).forEach(([k, v]) => { if (v?.trim()) cleaned[k] = v.trim(); });
    setSocials(cleaned); saveProfile({ socials: cleaned }); setOpenModal(null);
  };
  const commitWidgets = () => {
    setWidgets(draftWidgets);
    try { localStorage.setItem(`osu_widgets_${user.id}`, JSON.stringify(draftWidgets)); } catch {}
    saveProfile({ widgets: draftWidgets });
    setOpenModal(null);
  };
  const commitColor   = (c) => { setColor(c); saveProfile({ color: c }); setOpenModal(null); };
  const commitFavMap  = async () => {
    const url = draftFavUrl.trim(); if (!url) return;
    const match = url.match(/beatmapsets?\/([\d]+)/);
    const id = match?.[1];
    const mapData = { url, id: id || null, title: id ? `Map #${id}` : 'Favourite Map', artist: '', stars: null, bpm: null, cover_url: id ? `https://assets.ppy.sh/beatmaps/${id}/covers/cover.jpg` : null };
    setFavMap(mapData); saveProfile({ fav_map: mapData }); setOpenModal(null);
  };
  const removeFavMap  = () => { setFavMap(null); saveProfile({ fav_map: null }); };
  const commitBanner  = () => {
    const url = draftBannerUrl.trim() || null;
    const val = url ? { url, dim: draftBannerDim, posY: draftBannerPosY } : null;
    setCustomBanner(val);
    saveProfile({ custom_banner: val ? JSON.stringify(val) : null });
    // Сохраняем в localStorage сразу — без F5
    try {
      const sess = JSON.parse(localStorage.getItem('osu_session') || '{}');
      sess.profileCustomBanner = val ? JSON.stringify(val) : null;
      localStorage.setItem('osu_session', JSON.stringify(sess));
    } catch {}
    setOpenModal(null);
  };
  const removeBanner  = () => {
    setCustomBanner(null);
    saveProfile({ custom_banner: null });
    try { const sess = JSON.parse(localStorage.getItem('osu_session') || '{}'); sess.profileCustomBanner = null; localStorage.setItem('osu_session', JSON.stringify(sess)); } catch {}
  };
  const saveSetup = (s) => {
    setSetup(s);
    if (!isReadOnly && user?.id) {
      try { localStorage.setItem(`osu_setup_${user.id}`, JSON.stringify(s)); } catch {}
    }
    saveProfile({ setup: s });
  };

  /* DnD handler — только для своего профиля */
  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setWidgets(prev => {
      const next = arrayMove(prev, prev.indexOf(active.id), prev.indexOf(over.id));
      try { localStorage.setItem(`osu_widgets_${user.id}`, JSON.stringify(next)); } catch {}
      saveProfile({ widgets: next });
      return next;
    });
  };

  const toggleDraftWidget = (key) =>
    setDraftWidgets(p => p.includes(key) ? p.filter(w => w !== key) : [...p, key]);

  if (!user) return (
    <div className="card" style={{ padding: 60, textAlign: 'center' }}>
      <div style={{ color: 'var(--muted)', fontSize: 13 }}>Loading...</div>
    </div>
  );

  const p = user;
  const initial    = (p.username || 'S').charAt(0).toUpperCase();
  const flagMap    = { RU:'🇷🇺', JP:'🇯🇵', AM:'🇦🇲', US:'🇺🇸', KR:'🇰🇷', DE:'🇩🇪', FR:'🇫🇷', GB:'🇬🇧', PL:'🇵🇱', CN:'🇨🇳', AU:'🇦🇺', CA:'🇨🇦', BR:'🇧🇷', UA:'🇺🇦', FI:'🇫🇮', SE:'🇸🇪', NO:'🇳🇴' };
  const flag       = flagMap[p.country_code] || p.country_code || '🏳️';
  const accentColor = color || theme.accent;
  const accentDim   = color ? `${color}22` : theme.accentDim;
  const osuJoined   = p.joined ? `${lang === 'ru' ? 'osu! с' : 'osu! since'} ${p.joined}` : null;
  const trackerDate = p.trackerJoinedAt
    ? new Date(p.trackerJoinedAt).toLocaleDateString(lang === 'ru' ? 'ru-RU' : 'en-US', { year: 'numeric', month: 'short' })
    : null;

  /* ── Размеры ячеек с учётом мобильного ── */
  const gridCols = isMobile ? 2 : 4;
  const colSpanOf = (size) => {
    if (size === 'large')  return isMobile ? 2 : 4;
    if (size === 'medium') return 2;
    return 1;
  };

  /* ══ РЕНДЕР СЕТКИ ВИДЖЕТОВ ══════════════════════════════
     Архитектура flex chain:

     <div gridCell>               display:flex; align-self:stretch
       <SortableWidget>           display:flex; flex-col; flex:1; h:100%
         <WidgetXxx.card>         flex:1  ← заполняет всё

     Итог: все карточки в одной строке одинаковой высоты,
     фон и бордер растянуты, нет пустых мест.
  ══════════════════════════════════════════════════════ */
  const renderWidgets = () => widgets.map(key => {
    const WComp = WIDGET_COMPONENTS[key];
    if (!WComp) return null;
    const size    = WIDGET_META[key]?.size || 'small';
    const colSpan = colSpanOf(size);

    const inner = key === 'setup'
      ? <WComp user={{ ...p, profileSetup: setup }} lang={lang} isReadOnly={isReadOnly} onSave={saveSetup} />
      : <WComp user={p} lang={lang} isReadOnly={isReadOnly} />;

    return (
      <div
        key={key}
        style={{
          gridColumn: `span ${colSpan}`,
          /* flex chain: ячейка-обёртка */
          display: 'flex',
          alignSelf: 'stretch',
        }}
      >
        {isReadOnly
          ? <StaticWidget>{inner}</StaticWidget>
          : <SortableWidget id={key} isReadOnly={isReadOnly}>{inner}</SortableWidget>
        }
      </div>
    );
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {notif && <Notif msg={notif} onClose={() => setNotif(null)} />}

      {/* ══ MODALS ══════════════════════════════════════════════ */}

      {openModal === 'status' && (
        <Modal title={lang === 'ru' ? '✎ Статус' : '✎ Status'} onClose={() => setOpenModal(null)} width={320}>
          <input autoFocus value={draftStatus} onChange={e => setDraftStatus(e.target.value.slice(0, 100))}
            onKeyDown={e => { if (e.key === 'Enter') commitStatus(); }}
            placeholder={lang === 'ru' ? 'Твой статус...' : 'Your status...'}
            style={{ width: '100%', padding: '10px 12px', borderRadius: 10, fontSize: 13, background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }} />
          <div style={{ fontSize: 10, color: 'var(--muted)', textAlign: 'right', marginTop: 4 }}>{draftStatus.length}/100</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 10 }}>
            {STATUS_IDEAS.map(s => <button key={s} onClick={() => setDraftStatus(s)} style={{ fontSize: 10, padding: '3px 9px', borderRadius: 20, cursor: 'pointer', background: 'var(--dim)', border: '1px solid var(--border)', color: 'var(--muted)', fontFamily: 'inherit' }}>{s}</button>)}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button onClick={commitStatus} className="btn-accent" style={{ flex: 1, padding: '9px', fontSize: 13, borderRadius: 10 }}>{lang === 'ru' ? 'Сохранить' : 'Save'}</button>
            <button onClick={() => { setDraftStatus(''); commitStatus(); }} className="btn-ghost" style={{ padding: '9px 14px', fontSize: 13, borderRadius: 10 }}>✕</button>
          </div>
        </Modal>
      )}

      {openModal === 'socials' && (
        <Modal title={lang === 'ru' ? '✎ Соцсети' : '✎ Socials'} onClose={() => setOpenModal(null)} width={360}>
          {Object.entries(SOCIAL_META).map(([key, meta]) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 8, background: meta.bg, color: meta.color, flexShrink: 0 }}>{meta.icon}</span>
              <input value={draftSocials[key] || ''} onChange={e => setDraftSocials(d => ({ ...d, [key]: e.target.value }))}
                placeholder={meta.placeholder}
                style={{ flex: 1, padding: '7px 10px', borderRadius: 8, fontSize: 12, background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none', fontFamily: 'inherit' }} />
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button onClick={commitSocials} className="btn-accent" style={{ flex: 1, padding: '9px', fontSize: 13, borderRadius: 10 }}>{lang === 'ru' ? 'Сохранить' : 'Save'}</button>
            <button onClick={() => setOpenModal(null)} className="btn-ghost" style={{ padding: '9px 14px', fontSize: 13, borderRadius: 10 }}>{lang === 'ru' ? 'Отмена' : 'Cancel'}</button>
          </div>
        </Modal>
      )}

      {openModal === 'widgets' && (
        <Modal title={lang === 'ru' ? '⊞ Виджеты' : '⊞ Widgets'} onClose={() => setOpenModal(null)} width={360}>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 12 }}>
            {lang === 'ru' ? 'Включи нужные виджеты. Порядок меняй прямо на странице профиля.' : 'Toggle widgets here. Drag to reorder on the profile page.'}
          </div>
          {ALL_WIDGET_KEYS.map(key => {
            const m = WIDGET_META[key];
            const active = draftWidgets.includes(key);
            return (
              <div key={key} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px', borderRadius: 10,
                background: active ? 'var(--dim)' : 'var(--card)',
                border: `1px solid ${active ? 'var(--border)' : 'rgba(255,255,255,0.04)'}`,
                marginBottom: 6, cursor: 'pointer', transition: 'all 0.15s',
              }} onClick={() => toggleDraftWidget(key)}>
                <div style={{
                  width: 20, height: 20, borderRadius: 6, flexShrink: 0,
                  background: active ? 'var(--a)' : 'var(--bg2)',
                  border: `1px solid ${active ? 'var(--a)' : 'var(--border)'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, color: active ? 'var(--bg)' : 'var(--muted)', fontWeight: 700,
                }}>{active ? '✓' : ''}</div>
                <span style={{ flex: 1, fontSize: 13, fontWeight: active ? 700 : 400 }}>{lang === 'ru' ? m.labelRu : m.label}</span>
                <span style={{ fontSize: 9, color: m.size === 'large' ? 'var(--amber)' : m.size === 'medium' ? 'var(--a)' : 'var(--muted)', padding: '1px 7px', borderRadius: 20, background: 'var(--bg2)', border: '1px solid var(--border)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.3 }}>
                  {m.size}
                </span>
              </div>
            );
          })}
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button onClick={commitWidgets} className="btn-accent" style={{ flex: 1, padding: '9px', fontSize: 13, borderRadius: 10 }}>{lang === 'ru' ? 'Применить' : 'Apply'}</button>
            <button onClick={() => setOpenModal(null)} className="btn-ghost" style={{ padding: '9px 14px', fontSize: 13, borderRadius: 10 }}>{lang === 'ru' ? 'Отмена' : 'Cancel'}</button>
          </div>
        </Modal>
      )}

      {openModal === 'color' && (
        <Modal title={lang === 'ru' ? '● Цвет профиля' : '● Profile Color'} onClose={() => setOpenModal(null)} width={280}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
            {PRESET_COLORS.map(c => (
              <button key={c} onClick={() => commitColor(c)} style={{ width: 36, height: 36, borderRadius: 10, background: c, border: `3px solid ${c === draftColor ? 'white' : 'transparent'}`, cursor: 'pointer' }} />
            ))}
            <input type="color" value={draftColor || '#ff6eb4'} onChange={e => setDraftColor(e.target.value)}
              style={{ width: 36, height: 36, borderRadius: 10, cursor: 'pointer', border: 'none', padding: 0 }} />
            <button onClick={() => commitColor(null)} style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--bg2)', border: '1px solid var(--border)', cursor: 'pointer', fontSize: 14, color: 'var(--muted)' }}>✕</button>
          </div>
          {draftColor && <button onClick={() => commitColor(draftColor)} className="btn-accent" style={{ width: '100%', padding: '9px', fontSize: 13, borderRadius: 10 }}>{lang === 'ru' ? 'Применить' : 'Apply'}</button>}
        </Modal>
      )}

      {openModal === 'favmap' && (
        <Modal title={lang === 'ru' ? '♥ Любимая карта' : '♥ Favourite Map'} onClose={() => setOpenModal(null)} width={380}>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 14 }}>
            {lang === 'ru' ? 'Вставь ссылку или выбери ♥ в каталоге карт' : 'Paste link or pick ♥ in the map catalog'}
          </p>
          <input autoFocus value={draftFavUrl} onChange={e => setDraftFavUrl(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') commitFavMap(); }}
            placeholder="https://osu.ppy.sh/beatmapsets/12345"
            style={{ width: '100%', padding: '10px 12px', borderRadius: 10, fontSize: 12, background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box' }} />
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button onClick={commitFavMap} className="btn-accent" style={{ flex: 1, padding: '9px', fontSize: 13, borderRadius: 10 }} disabled={!draftFavUrl.trim()}>{lang === 'ru' ? 'Добавить' : 'Add'}</button>
            <button onClick={() => setOpenModal(null)} className="btn-ghost" style={{ padding: '9px 14px', fontSize: 13, borderRadius: 10 }}>{lang === 'ru' ? 'Отмена' : 'Cancel'}</button>
          </div>
        </Modal>
      )}

      {openModal === 'banner' && (
        <Modal title={lang === 'ru' ? '◈ Кастомный баннер' : '◈ Custom Banner'} onClose={() => setOpenModal(null)} width={400}>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 14 }}>
            {lang === 'ru'
              ? 'Вставь прямую ссылку на картинку (imgur, catbox, etc). Рекомендуется 1500×500px.'
              : 'Paste a direct image link (imgur, catbox, etc). Recommended 1500×500px.'}
          </p>
          <input autoFocus value={draftBannerUrl} onChange={e => setDraftBannerUrl(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') commitBanner(); }}
            placeholder="https://i.imgur.com/..."
            style={{ width: '100%', padding: '10px 12px', borderRadius: 10, fontSize: 12, background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box' }} />
          {draftBannerUrl && (
            <div
              style={{ marginTop: 12, borderRadius: 10, overflow: 'hidden', height: 100, border: isDraggingPreview ? '1.5px solid var(--a)' : '1px solid var(--border)', position: 'relative', cursor: isDraggingPreview ? 'grabbing' : 'grab', userSelect: 'none' }}
              onMouseDown={e => {
                setIsDraggingPreview(true);
                dragStartRef.current = { y: e.clientY, posY: draftBannerPosY };
                e.preventDefault();
              }}
              onMouseMove={e => {
                if (!isDraggingPreview) return;
                const delta = dragStartRef.current.y - e.clientY;
                const newPosY = Math.max(0, Math.min(100, dragStartRef.current.posY + delta * 0.5));
                setDraftBannerPosY(newPosY);
              }}
              onMouseUp={() => setIsDraggingPreview(false)}
              onMouseLeave={() => setIsDraggingPreview(false)}
            >
              <div style={{
                width: '100%', height: '100%',
                backgroundImage: `url(${draftBannerUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: `center ${draftBannerPosY}%`,
                backgroundRepeat: 'no-repeat',
              }} onError={() => {}} />
              <div style={{ position: 'absolute', inset: 0, background: `rgba(8,8,16,${draftBannerDim})`, borderRadius: 10, pointerEvents: 'none' }} />
              <div style={{ position: 'absolute', bottom: 6, left: 0, right: 0, textAlign: 'center', pointerEvents: 'none' }}>
                <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', background: 'rgba(0,0,0,0.4)', borderRadius: 8, padding: '2px 8px' }}>
                  {isDraggingPreview ? '↕ drag' : '↕ drag to reposition'}
                </span>
              </div>
            </div>
          )}
          <div style={{ marginTop: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600 }}>{lang === 'ru' ? 'Затемнение' : 'Darkness'}</span>
              <span style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'monospace' }}>{Math.round(draftBannerDim * 100)}%</span>
            </div>
            <input type="range" min={0} max={100} value={Math.round(draftBannerDim * 100)}
              onChange={e => setDraftBannerDim(Number(e.target.value) / 100)}
              style={{ width: '100%', accentColor: 'var(--a)', cursor: 'pointer' }} />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button onClick={commitBanner} className="btn-accent" style={{ flex: 1, padding: '9px', fontSize: 13, borderRadius: 10 }} disabled={!draftBannerUrl.trim()}>{lang === 'ru' ? 'Применить' : 'Apply'}</button>
            {customBanner && <button onClick={() => { setDraftBannerUrl(''); removeBanner(); }} className="btn-ghost" style={{ padding: '9px 14px', fontSize: 13, borderRadius: 10 }}>✕</button>}
            <button onClick={() => setOpenModal(null)} className="btn-ghost" style={{ padding: '9px 14px', fontSize: 13, borderRadius: 10 }}>{lang === 'ru' ? 'Отмена' : 'Cancel'}</button>
          </div>
        </Modal>
      )}

      {/* ══ HERO CARD ═══════════════════════════════════════════ */}
      <div className="card fu" style={{
        padding: 0, overflow: 'hidden', position: 'relative',
        border: color ? `1px solid ${color}40` : undefined,
      }}>

        {/* ── Баннер — полный фон карточки (кастомный приоритетнее osu!) ── */}
        {(customBanner?.url || p.cover_url) && (
          <div style={{
            position: 'absolute', inset: 0,
            backgroundImage: `url(${customBanner?.url || p.cover_url})`,
            backgroundPositionY: customBanner?.url ? `${customBanner.posY ?? 30}%` : '30%',
            backgroundSize: 'cover', backgroundPosition: 'center 30%',
            zIndex: 0,
          }} />
        )}
        {/* Тёмный оверлей поверх баннера */}
        <div style={{
          position: 'absolute', inset: 0, zIndex: 1,
          background: (customBanner?.url || p.cover_url)
            ? `linear-gradient(135deg, rgba(8,8,16,${(customBanner?.dim ?? 0.78) + 0.08}) 0%, rgba(8,8,16,${customBanner?.dim ?? 0.78}) 100%)`
            : `radial-gradient(ellipse at 30% 50%, ${accentDim} 0%, transparent 65%)`,
          pointerEvents: 'none',
        }} />
        {/* Акцентный градиент */}
        <div style={{ position: 'absolute', inset: 0, zIndex: 1, background: `radial-gradient(ellipse at 20% 60%, ${accentDim} 0%, transparent 55%)`, pointerEvents: 'none' }} />

        {/* ── Контент поверх баннера ── */}
        <div style={{ position: 'relative', zIndex: 2, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 0 }}>

          {/* ── ЛЕВАЯ КОЛОНКА ── */}
          <div style={{ padding: 24, borderRight: isMobile ? 'none' : '1px solid rgba(255,255,255,0.08)', borderBottom: isMobile ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
              <div style={{
                width: 76, height: 76, borderRadius: 20, flexShrink: 0,
                background: p.avatar_url ? `url(${p.avatar_url}) center/cover` : 'var(--grad)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 28, color: 'var(--bg)', fontWeight: 800, overflow: 'hidden',
                border: `2px solid ${accentColor}70`,
                boxShadow: `0 0 28px ${accentDim}, 0 4px 16px rgba(0,0,0,0.5)`,
              }}>{!p.avatar_url && initial}</div>
              <div style={{ flex: 1, minWidth: 0 }}>

                {/* Ник + флаг + клан */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                  <a href={`https://osu.ppy.sh/users/${p.id}`} target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: 20, fontWeight: 800, fontFamily: "'Syne',sans-serif", color: 'var(--text)', textDecoration: 'none', borderBottom: `2px solid ${accentColor}60` }}>
                    {p.username}
                  </a>
                  <span style={{ fontSize: 16 }}>{flag}</span>
                  {p.clan && (
                    <a href={`https://osu.ppy.sh/teams/${p.clan.id}`} target="_blank" rel="noopener noreferrer"
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '4px 11px 4px 7px', borderRadius: 20, textDecoration: 'none',
                        background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(8px)',
                        border: `1.5px solid ${accentColor}55`,
                      }}>
                      {p.clan.icon
                        ? <img src={p.clan.icon} alt="" style={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0 }} />
                        : <span style={{ width: 20, height: 20, borderRadius: '50%', background: accentColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 800, color: '#fff', flexShrink: 0 }}>{(p.clan.tag||'?').charAt(0)}</span>
                      }
                      <span style={{ fontSize: 13, fontWeight: 800, color: accentColor, letterSpacing: 0.3 }}>[{p.clan.tag}]</span>
                      <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.clan.name}</span>
                    </a>
                  )}
                </div>

                {/* Статус */}
                <div style={{ marginBottom: 8 }}>
                  {status ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: `${accentColor}18`, border: `1px solid ${accentColor}40`, color: 'var(--text)' }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: accentColor, flexShrink: 0, boxShadow: `0 0 6px ${accentColor}` }} />
                        {status}
                      </span>
                      {!isReadOnly && <button onClick={() => openEdit('status')} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 11, padding: 0 }}>✎</button>}
                    </div>
                  ) : (
                    !isReadOnly && <EditBtn onClick={() => openEdit('status')} label={lang === 'ru' ? 'Добавить статус' : 'Add status'} />
                  )}
                </div>

                {/* osu! since — просто текст */}
                {osuJoined && <div style={{ fontSize: 11, color: 'rgba(148,163,184,0.5)', marginBottom: 8 }}>{osuJoined}</div>}

                {/* Любимая карта */}
                {favMap
                  ? <FavMapCard map={favMap} onRemove={isReadOnly ? null : removeFavMap} isReadOnly={isReadOnly} lang={lang} accentColor={accentColor} />
                  : !isReadOnly && <EditBtn onClick={() => openEdit('favmap')} label={lang === 'ru' ? 'Любимая карта' : 'Favourite map'} color="#f87171" />
                }

                {/* Соцсети */}
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center', marginTop: 6 }}>
                  {Object.entries(SOCIAL_META).map(([key, meta]) => {
                    const val = socials?.[key]; if (!val) return null;
                    const href = meta.isLink ? `${meta.linkPrefix}${val.replace('@','')}` : null;
                    return (
                      <a key={key} href={href || undefined} target={href ? '_blank' : undefined} rel="noopener noreferrer"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 20, background: meta.bg, border: `1px solid ${meta.color}40`, color: meta.color, fontSize: 11, textDecoration: 'none', fontWeight: 600 }}>
                        {meta.icon} {val}
                      </a>
                    );
                  })}
                  {!isReadOnly && (
                    <button onClick={() => openEdit('socials')} title={lang === 'ru' ? 'Редактировать соцсети' : 'Edit socials'} style={{
                      background: 'none', border: '1px dashed rgba(255,255,255,0.2)',
                      borderRadius: '50%', width: 24, height: 24, cursor: 'pointer',
                      color: 'rgba(148,163,184,0.5)', fontSize: 12, display: 'flex',
                      alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>✎</button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── ПРАВАЯ КОЛОНКА ── */}
          <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              {[
                { l: lang === 'ru' ? 'Глобальный' : 'Global',  v: `#${fmt(p.global_rank  || 0)}`, c: accentColor },
                { l: lang === 'ru' ? 'По стране'  : 'Country', v: `#${fmt(p.country_rank || 0)}`, c: '#fbbf24'   },
                { l: 'PP',                                       v: fmt(Math.round(p.pp || 0)),     c: '#68d891'   },
              ].map(r => (
                <div key={r.l} style={{ flex: 1, background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(8px)', borderRadius: 10, padding: '12px 10px', border: '1px solid rgba(255,255,255,0.08)', textAlign: 'center' }}>
                  <div className="mono" style={{ fontSize: 18, fontWeight: 700, color: r.c }}>{r.v}</div>
                  <div style={{ fontSize: 9, color: 'var(--muted)', marginTop: 3, letterSpacing: 0.5 }}>{r.l.toUpperCase()}</div>
                </div>
              ))}
            </div>
            {/* Grades row — крупнее в read-only (нет Customize блока) */}
            <div style={{ display: 'flex', gap: isReadOnly ? 8 : 5, justifyContent: 'center' }}>
              {[
                { l: 'SSH', v: p.grades?.SSH ?? 0, c: '#e0e0e0' },
                { l: 'SS',  v: p.grades?.SS  ?? 0, c: '#f5e642' },
                { l: 'SH',  v: p.grades?.SH  ?? 0, c: '#c0c0c0' },
                { l: 'S',   v: p.grades?.S   ?? 0, c: '#f5e642' },
                { l: 'A',   v: p.grades?.A   ?? 0, c: '#68d891' },
              ].map(g => (
                <div key={g.l} style={{
                  flex: 1, background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(8px)',
                  borderRadius: isReadOnly ? 12 : 8,
                  padding: isReadOnly ? '14px 8px' : '8px 4px',
                  border: '1px solid rgba(255,255,255,0.07)', textAlign: 'center',
                }}>
                  <div className="mono" style={{ fontSize: isReadOnly ? 18 : 13, fontWeight: 800, color: g.c }}>{fmt(g.v)}</div>
                  <div style={{ fontSize: isReadOnly ? 10 : 9, color: 'rgba(148,163,184,0.55)', marginTop: 3, fontWeight: 700, letterSpacing: 0.5 }}>{g.l}</div>
                </div>
              ))}
            </div>

            {!isReadOnly && (
              <div style={{ padding: '10px 12px', borderRadius: 12, background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(8px)', border: '1px dashed rgba(255,255,255,0.12)' }}>
                <div style={{ fontSize: 9, color: 'var(--muted)', marginBottom: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>✎ {lang === 'ru' ? 'Кастомизация' : 'Customize'}</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button onClick={() => openEdit('widgets')} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '5px 13px', borderRadius: 20, cursor: 'pointer',
                    background: 'var(--dim)', border: `1.5px dashed var(--a)`,
                    color: 'var(--a3)', fontSize: 12, fontFamily: 'inherit', fontWeight: 700, transition: 'all 0.18s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; e.currentTarget.style.boxShadow = '0 0 10px rgba(255,255,255,0.08)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'var(--dim)'; e.currentTarget.style.boxShadow = 'none'; }}
                  >⊞ {lang === 'ru' ? 'Виджеты' : 'Widgets'}</button>
                  <button onClick={() => openEdit('banner')} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '5px 13px', borderRadius: 20, cursor: 'pointer',
                    background: customBanner?.url ? 'rgba(255,255,255,0.07)' : 'var(--dim)',
                    border: `1.5px dashed ${customBanner?.url ? 'rgba(255,255,255,0.4)' : 'var(--a)'}`,
                    color: customBanner?.url ? 'var(--text)' : 'var(--a3)', fontSize: 12, fontFamily: 'inherit', fontWeight: 700, transition: 'all 0.18s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = customBanner?.url ? 'rgba(255,255,255,0.07)' : 'var(--dim)'; }}
                  >◧ {customBanner?.url ? (lang === 'ru' ? 'Баннер ✓' : 'Banner ✓') : (lang === 'ru' ? 'Баннер' : 'Banner')}</button>
                  <button onClick={() => openEdit('color')} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '5px 13px', borderRadius: 20, cursor: 'pointer',
                    background: color ? `${color}25` : 'var(--dim)',
                    border: `1.5px dashed ${color || 'var(--a)'}`,
                    color: color || 'var(--a3)', fontSize: 12, fontFamily: 'inherit', fontWeight: 700, transition: 'all 0.18s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; e.currentTarget.style.boxShadow = '0 0 10px rgba(255,255,255,0.08)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = color ? `${color}25` : 'var(--dim)'; e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: color || 'var(--a)', display: 'inline-block', boxShadow: color ? `0 0 6px ${color}` : 'none' }} />
                    {lang === 'ru' ? 'Цвет' : 'Color'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ══ WIDGETS — Bento Grid ════════════════════════════════
          display: grid
          grid-template-columns: repeat(4, 1fr)   [2 на мобиле]
          grid-auto-flow: dense                    ← закрывает дыры
          gap: 12px

          align-items НЕ задан → default = stretch
          → все ячейки в строке одинаковой высоты
      ══════════════════════════════════════════════════════ */}
      {isReadOnly ? (
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
          gridAutoFlow: 'dense',
          columnGap: 12,
          rowGap: 16,
        }}>
          {renderWidgets()}
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={widgets} strategy={rectSortingStrategy}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
              gridAutoFlow: 'dense',
              columnGap: 12,
              rowGap: 16,
            }}>
              {renderWidgets()}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};

export default Profile;